// src/App.jsx
import './sentry.js';
import * as Sentry from '@sentry/react';
import React, { Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Styles
import './styles/styles.css';

// Core Components & Contexts
import LoadingScreen from './components/common/LoadingScreen.jsx';
import ErrorBoundary from './components/common/ErrorBoundary.jsx';
import FirebaseInitializer from './router/FirebaseInitializer.jsx';
import Header from './components/layout/Header.jsx';
import Footer from './components/layout/Footer.jsx';
import { UserProvider } from './contexts/UserContext.jsx';
import { GuestProvider } from './contexts/GuestContext.jsx';
import { LoadingProvider } from './contexts/LoadingContext.jsx';
import { StripePlansProvider } from './contexts/StripePlansContext.jsx';
import { ChatProvider } from './contexts/ChatContext.jsx';
import StableStripeProvider from './components/stripe/StableStripeProvider.jsx';
import I18nProvider from './i18n/index.js';
import { ThemeProvider } from './contexts/ThemeContext.jsx';
import { ProtectedRoute } from './router/ProtectedRoute.jsx';

// --- Lazy Load ALL Route Components ---
const LandingPage = lazy(() => import('./pages/LandingPage.jsx'));
const LoginPage = lazy(() => import('./pages/LoginPage.jsx'));
const OAuthCallback = lazy(() => import('./components/OAuthCallback.jsx'));
const ReferralRedirect = lazy(() => import('./components/ReferralRedirect.jsx'));
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const AgentWorkspacePage = lazy(() => import('./pages/AgentWorkspacePage.jsx'));
const PaymentPage = lazy(() => import('./pages/PaymentPage.jsx'));
const PricingPage = lazy(() => import('./pages/PricingPage.jsx'));
const AboutPage = lazy(() => import('./pages/AboutPage.jsx'));
const ContactPage = lazy(() => import('./pages/ContactPage.jsx'));
const FAQPage = lazy(() => import('./pages/FAQPage.jsx'));
const TeamPage = lazy(() => import('./pages/TeamPage.jsx'));
const PackagesPage = lazy(() => import('./pages/PackagesPage.jsx'));
const AgentDetailsPage = lazy(() => import('./pages/AgentDetailsPage.jsx'));
const TermsPage = lazy(() => import('./pages/TermsPage.jsx'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage.jsx'));
const CookiesPage = lazy(() => import('./pages/CookiesPage.jsx'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage.jsx'));
// const ProfilePage = lazy(() => import('./pages/ProfilePage.jsx')); // Temporarily disabled
const ReferralsPage = lazy(() => import('./pages/ReferralsPage.jsx'));
const BlogPage = lazy(() => import('./pages/BlogPage.jsx'));
const DocsPage = lazy(() => import('./pages/DocsPage.jsx'));
const ApiPage = lazy(() => import('./pages/ApiPage.jsx'));
const StatusPage = lazy(() => import('./pages/StatusPage.jsx'));
const CommunityPage = lazy(() => import('./pages/CommunityPage.jsx'));
const OnboardAgentPage = lazy(() => import('./pages/OnboardAgentPage.jsx'));
const CreditsPage = lazy(() => import('./pages/CreditsPage.jsx'));
// GURU FIX: This import now correctly uses PascalCase, the standard for React components.
// Ensure your file is named 'AddBusinessPage.jsx' in the 'src/pages/' directory.
const AddBusinessPage = lazy(() => import('./pages/addBusinessPage.jsx')); 
const WorkspaceSettingsPage = lazy(() => import('./pages/WorkspaceSettingsPage.jsx'));
const AgentSettingsPage = lazy(() => import('./pages/AgentSettingsPage.jsx'));
const AgentFineTuningPage = lazy(() => import('./pages/AgentFineTuningPage.jsx'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage.jsx'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage.jsx'));
const IntegrationSettingsPage = lazy(() => import('./pages/IntegrationSettingsPage.jsx'));
const TestimonialsPage = lazy(() => import('./pages/TestimonialsPage.jsx'));

// --- Create a QueryClient instance with sensible defaults ---
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const AppContent = () => {
  const location = useLocation();
  const noLayoutRoutes = ['/login', '/auth/callback', '/notfound'];

  const showHeader = !noLayoutRoutes.some(route => location.pathname.startsWith(route));
  const showFooter = !noLayoutRoutes.some(route => location.pathname.startsWith(route));

  return (
    <div className="app-container flex flex-col min-h-screen bg-[var(--bg-primary)] transition-colors duration-300">
      {showHeader && <Header />}
      <main id="main-content" role="main" className="flex-grow">
        <Suspense fallback={<LoadingScreen type="page" />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<OAuthCallback />} />
            
            {/* Referral Redirect Route */}
            <Route path="/ref/:code" element={<ReferralRedirect />} />
            
            {/* Public Informational Pages */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/packages" element={<PackagesPage />} />
            <Route path="/agent/:agentName" element={<AgentDetailsPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/api" element={<ApiPage />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            
            {/* Protected Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/workspace/:workspaceId/:agentId" element={<ProtectedRoute><AgentWorkspacePage /></ProtectedRoute>} />
            <Route path="/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
            {/* <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} /> */}
            <Route path="/referrals" element={<ProtectedRoute><ReferralsPage /></ProtectedRoute>} />
            <Route path="/onboard-agent" element={<ProtectedRoute><OnboardAgentPage /></ProtectedRoute>} />
            <Route path="/credits" element={<ProtectedRoute><CreditsPage /></ProtectedRoute>} />
            
            {/* Settings & Business Hub Routes */}
            <Route path="/settings/workspace/:workspaceId" element={<ProtectedRoute><WorkspaceSettingsPage /></ProtectedRoute>} />
            <Route path="/settings/workspace/:workspaceId/agent/:agentId" element={<ProtectedRoute><AgentSettingsPage /></ProtectedRoute>} />
            <Route path="/settings/workspace/:workspaceId/agent/:agentId/fine-tuning" element={<ProtectedRoute><AgentFineTuningPage /></ProtectedRoute>} />
            <Route path="/settings/workspace/:workspaceId/add-business" element={<ProtectedRoute><AddBusinessPage /></ProtectedRoute>} />
            <Route path="/settings/workspace/:workspaceId/integrations" element={<ProtectedRoute><IntegrationSettingsPage /></ProtectedRoute>} />

            {/* 404 Catch-all */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      {showFooter && <Footer />}
    </div>
  );
};

const App = () => (
  <Router>
    <ErrorBoundary>
      {/* --- Wrap the entire app with the QueryClientProvider --- */}
      <QueryClientProvider client={queryClient}>
        <LoadingProvider>
          <FirebaseInitializer>
            <GuestProvider>
              <UserProvider>
                <ThemeProvider>
                  <I18nProvider>
                    <ChatProvider>
                    <StableStripeProvider>
                      <StripePlansProvider>
                        <AppContent />
                      </StripePlansProvider>
                    </StableStripeProvider>
                    </ChatProvider>
                  </I18nProvider>
                </ThemeProvider>
              </UserProvider>
            </GuestProvider>
          </FirebaseInitializer>
        </LoadingProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </Router>
);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Fatal Error: Root element with id 'root' not found in the DOM.");
}

const root = createRoot(rootElement);
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

export default App;